import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError, downloadBlob } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Download } from "@phosphor-icons/react";

const iso = (d) => d.toISOString().slice(0, 10);
const ACTION_LABEL = {
  claro_invite: "Claro staff invited", claro_invite_accept: "Claro staff accepted", claro_invite_decline: "Claro staff declined",
  claro_invite_cancel: "Invitation cancelled", revoke: "Claro staff access removed", seat_role_change: "Role changed",
  seat_invite: "User invited", seat_remove: "User removed", create: "User created", delete: "User deleted", update: "User updated",
  deactivate: "User deactivated", reactivate: "User reactivated", unlock: "Account unlocked", mfa_reset: "MFA reset",
  password_reset_forced: "Password reset forced",
};

export function AccessReport() {
  const [start, setStart] = useState(iso(new Date(Date.now() - 90 * 864e5)));
  const [end, setEnd] = useState(iso(new Date()));
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const load = useCallback(() => {
    const my = ++reqId.current;
    setLoading(true);
    api.get("/admin/access-report", { params: { start, end } })
      .then(({ data }) => { if (my === reqId.current) setRep(data); })
      .catch((e) => { if (my === reqId.current) { setRep({ visits: [], changes: [] }); toast.error(formatApiError(e.response?.data?.detail)); } })
      .finally(() => { if (my === reqId.current) setLoading(false); });
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  const th = "px-3 py-2 text-left font-semibold whitespace-nowrap";

  return (
    <div className="space-y-6" data-testid="access-report">
      <div className="flex flex-wrap items-end gap-3">
        <div><div className="text-xs mb-1">From</div><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-none w-44" data-testid="access-report-start" /></div>
        <div><div className="text-xs mb-1">To</div><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-none w-44" data-testid="access-report-end" /></div>
        <Button variant="outline" className="rounded-none" data-testid="access-report-export"
          onClick={() => downloadBlob(`/admin/access-report/export.xlsx?start=${start}&end=${end}`, `access_report_${start}_${end}.xlsx`)}>
          <Download size={14} className="mr-1.5" />Export Excel
        </Button>
        {loading && <span className="text-xs text-muted-foreground" data-testid="access-report-loading">Loading…</span>}
      </div>
      <section>
        <h3 className="font-semibold mb-2">Daily access <span className="text-xs font-normal text-muted-foreground">(who opened this entity — Claro staff and your users)</span></h3>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs min-w-[760px]">
            <thead className="bg-muted/60"><tr>{["Date", "Name", "Email", "Type", "Role", "First (UTC)", "Last (UTC)", "Active periods"].map((h) => <th key={h} className={th} title={h === "Active periods" ? "Number of 5-minute periods with activity that day" : undefined}>{h}</th>)}</tr></thead>
            <tbody>
              {(rep?.visits || []).map((v) => (
                <tr key={`${v.date}-${v.user_id}`} className="border-t border-border" data-testid={`access-visit-${v.date}-${v.user_id}`}>
                  <td className="px-3 py-1.5 whitespace-nowrap">{v.date}</td><td className="px-3 py-1.5">{v.name}</td><td className="px-3 py-1.5">{v.email}</td>
                  <td className="px-3 py-1.5">{v.is_claro_staff ? <span className="border border-indigo-300 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-700">Claro staff</span> : "Entity user"}</td>
                  <td className="px-3 py-1.5">{v.role}</td><td className="px-3 py-1.5 whitespace-nowrap">{(v.first_at || "").slice(11, 19)}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{(v.last_at || "").slice(11, 19)}</td><td className="px-3 py-1.5">{v.visits}</td>
                </tr>
              ))}
              {rep && !rep.visits.length && <tr><td colSpan={8} className="px-3 py-4 text-center text-muted-foreground">No access recorded in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <section>
        <h3 className="font-semibold mb-2">Appointments, invitations &amp; role changes</h3>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs min-w-[760px]">
            <thead className="bg-muted/60"><tr>{["When (UTC)", "Action", "By", "Details"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>
              {(rep?.changes || []).map((c, i) => (
                <tr key={i} className="border-t border-border align-top">
                  <td className="px-3 py-1.5 whitespace-nowrap">{(c.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{ACTION_LABEL[c.action] || c.action}</td>
                  <td className="px-3 py-1.5">{c.user_name || c.user_email}</td><td className="px-3 py-1.5">{c.details}</td>
                </tr>
              ))}
              {rep && !rep.changes.length && <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">No changes in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
